import { useRef, useEffect, useState } from 'react';

import { ColorToken } from '@backrs_ui/common-ui/designSystem/colors/colors';
import { PanelNuggetActionParams } from '@backrs_ui/common-ui/src/models/nugget';

import { Typography, Box, SxProps, Theme } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { BaseNugget, BASE_NUGGET_IMAGE_MAX_HEIGHT } from './BaseNugget';
import { HoverButton } from './HoverButton';
import { SponsorChip, SponsorChipProps } from './SponsorChip';
import { TypeChip, TypeChipProps } from './TypeChip';

type LineItem = Readonly<{
  title: string;
  text: string;
}>;

type NuggetGenericProps = Readonly<{
  title: string;
  subTitle?: string;
  customHeader?: React.ReactNode;
  typeChipProps?: TypeChipProps[];
  sponsorChipProps?: SponsorChipProps[];
  bottomSponsorChipProps?: SponsorChipProps[];
  description?: string;
  lineItems?: LineItem[];
  coverImage?: string;
  onClick?: () => void;
  subText?: string;
  hideBody: boolean;
  fitContent: boolean;
  isHovered?: boolean;
  isHighlighted?: boolean;
  actionParams?: PanelNuggetActionParams;
  forceSubtextAtBottom?: boolean;
  coverImageBackgroundColor?: ColorToken;
}>;

export const NuggetGeneric = ({
  title,
  subTitle,
  customHeader,
  typeChipProps,
  sponsorChipProps,
  bottomSponsorChipProps,
  description,
  lineItems,
  coverImage,
  onClick,
  subText,
  hideBody,
  fitContent,
  isHovered,
  isHighlighted,
  actionParams,
  forceSubtextAtBottom,
  coverImageBackgroundColor,
}: NuggetGenericProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleChipCount, setVisibleChipCount] = useState<number>(typeChipProps?.length || 0);

  // This useEffect is to determine which chips to show based on how many fit on one line
  // Basically if a chip is overflowing, we don't render it at all
  useEffect(() => {
    if (!containerRef.current || !typeChipProps) return;

    const container = containerRef.current;
    const checkOverflow = () => {
      const containerRect = container.getBoundingClientRect();
      const containerRight = containerRect.right;

      const chipElements = Array.from(container.children);

      const visibleCount = chipElements.reduce((count, chipElement, index) => {
        const chipRect = chipElement.getBoundingClientRect();

        return chipRect.right <= containerRight ? index + 1 : count;
      }, 0);

      setVisibleChipCount(visibleCount);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [typeChipProps]);

  return (
    <BaseNugget
      subText={subText}
      onClick={onClick}
      fitContent={fitContent}
      forceSubtextAtBottom={forceSubtextAtBottom}
      isHighlighted={isHighlighted}
    >
      {coverImage && (
        <Box position="relative">
          <Box
            component="img"
            src={coverImage}
            alt={title}
            sx={coverImageSx(coverImageBackgroundColor)}
          />
          {sponsorChipProps && (
            <Box sx={sponsorChipOverlaySx}>
              {sponsorChipProps.map((sponsor) => (
                <SponsorChip
                  key={sponsor.label}
                  label={sponsor.label}
                  iconImage={sponsor.iconImage}
                  defaultIcon={sponsor.defaultIcon}
                />
              ))}
            </Box>
          )}
          {actionParams && (
            <HoverButton
              isVisible={isHovered ?? false}
              hasBookmarked={actionParams?.hasBookmarked}
              handleActionBookmark={actionParams?.handleActionBookmark}
              handleActionAddTask={actionParams?.handleActionAddTask}
              handleActionShare={actionParams?.handleActionShare}
            />
          )}
        </Box>
      )}
      {customHeader && customHeader}
      <Box sx={contentOuterSx}>
        <Box sx={contentInnerSx}>
          <Box sx={bodyContainerSx}>
            <Typography
              variant="text-xs-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {subTitle}
            </Typography>
            <Typography variant="text-xl-semibold" sx={titleSx}>
              {title}
            </Typography>
            {!hideBody && description && (
              <Box sx={bodyTextStyle}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography variant="text-sm-regular">{children}</Typography>
                    ),
                  }}
                >
                  {description}
                </ReactMarkdown>
              </Box>
            )}
            <Box sx={lineItemContainerSx}>
              {lineItems
                ?.filter((item) => item.title && item.text)
                .map((item) => (
                  <Box key={item.title} sx={lineItemSx}>
                    <Typography variant="text-sm-regular" sx={lineItemTextSx}>
                      <span style={lineItemTitleStyle}>{item.title}:&nbsp;</span>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>
          {typeChipProps && typeChipProps.length > 0 && (
            <Box ref={containerRef} sx={typeChipContainerSx}>
              {typeChipProps.slice(0, visibleChipCount).map((type) => (
                <TypeChip key={type.label} label={type.label} icon={type.icon} />
              ))}
            </Box>
          )}
          {bottomSponsorChipProps && bottomSponsorChipProps.length > 0 && (
            <Box sx={bottomSponsorChipContainerSx}>
              {bottomSponsorChipProps.map((sponsor) => (
                <SponsorChip
                  key={sponsor.label}
                  label={sponsor.label}
                  iconImage={sponsor.iconImage}
                  defaultIcon={sponsor.defaultIcon}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </BaseNugget>
  );
};

const coverImageSx =
  (coverImageBackgroundColor?: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '100%',
    aspectRatio: '16/9',
    maxHeight: BASE_NUGGET_IMAGE_MAX_HEIGHT,
    objectFit: 'cover',
    borderRadius: backrs.radius['radius-2xl'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: coverImageBackgroundColor
      ? backrs.colors[coverImageBackgroundColor]
      : backrs.colors['Gray/700'],
  });

const contentOuterSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  height: '100%',
  marginTop: backrs.spacing['spacing-md'],
});

const contentInnerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const titleSx: SxProps<Theme> = {
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  textOverflow: 'ellipsis',
};

const bodyTextStyle: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  textOverflow: 'ellipsis',
  color: backrs.colors['Gray/600'],
});

const lineItemSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0,
};

const lineItemTextSx: SxProps<Theme> = ({ backrs }) => ({
  m: 0,
  p: 0,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  textOverflow: 'ellipsis',
  color: backrs.colors['Gray/600'],
});

const lineItemTitleStyle: React.CSSProperties = {
  fontStyle: 'italic',
};

const typeChipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  columnGap: backrs.spacing['spacing-lg'],
  flexDirection: 'row',
  flexWrap: 'nowrap',
  overflow: 'hidden',
});

const sponsorChipOverlaySx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  bottom: backrs.spacing['spacing-2xl'],
  left: backrs.spacing['spacing-lg'],
  display: 'flex',
  columnGap: backrs.spacing['spacing-md'],
  flexDirection: 'row',
  flexWrap: 'wrap',
  rowGap: backrs.spacing['spacing-xs'],
  zIndex: 1,
});

const lineItemContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-none'],
});

const bottomSponsorChipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  marginTop: backrs.spacing['spacing-xs'],
});
